import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Search, Package, Truck, CheckCircle, Clock, AlertCircle, Eye, Phone, MapPin, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { OrderCountdownTimer, getCountdownStatus } from "@/components/orders/OrderCountdownTimer";
import { cn } from "@/lib/utils";

export function SellerOrderManagement() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [fulfillmentLoading, setFulfillmentLoading] = useState<string | null>(null);
  const [sellerDeliveryPreference, setSellerDeliveryPreference] = useState<string>('partner');

  useEffect(() => {
    if (user?.id) {
      fetchOrders();
      // Fetch seller's delivery preference
      supabase
        .from('profiles')
        .select('delivery_preference')
        .eq('id', user.id)
        .single()
        .then(({ data }) => {
          if (data?.delivery_preference) {
            setSellerDeliveryPreference(data.delivery_preference);
          }
        });
    }
  }, [user]);

  // Helpers
  const maskPhone = (phone: string | null | undefined): string => {
    if (!phone) return 'N/A';
    if (phone.length <= 4) return phone;
    return '****' + phone.slice(-4);
  };

  // Get full phone for sellers (they need it for delivery)
  const getFullPhone = (phone: string | null | undefined): string => {
    if (!phone) return 'N/A';
    return phone;
  };

  // Get item image from various possible sources
  const getItemImage = (item: any): string | null => {
    return item.image || 
           item.images?.[0] || 
           item.product?.images?.[0] || 
           item.productImage ||
           null;
  };

  const safeJsonParse = <T,>(val: unknown): T | null => {
    if (typeof val !== 'string') return null;
    try {
      return JSON.parse(val) as T;
    } catch {
      return null;
    }
  };

  const formatOrderDeliveryAddress = (order: any): string => {
    const shipping = order?.shippingAddress;
    if (shipping && typeof shipping === 'object') {
      const parts: string[] = [];
      const line1 = shipping.addressLine1 || shipping.address || '';
      const line2 = shipping.addressLine2 || '';
      const city = shipping.city || '';
      const state = shipping.state || '';
      const postalCode = shipping.postalCode || '';
      const country = shipping.country || '';

      const street = [line1, line2].filter(Boolean).join(', ');
      if (street) parts.push(street);
      if (city) parts.push(city);
      if (state) parts.push(state);
      if (postalCode) parts.push(postalCode);
      if (country && country !== 'Nigeria') parts.push(country);

      return parts.length ? parts.join(', ') : 'Address not available';
    }

    // deliveryAddress might be a JSON string from the DB
    const parsed = safeJsonParse<Record<string, any>>(order?.deliveryAddress) ?? order?.deliveryAddress;
    if (parsed && typeof parsed === 'object') {
      const parts: string[] = [];
      if (parsed.address) parts.push(parsed.address);
      if (parsed.street) parts.push(parsed.street);
      if (parsed.city) parts.push(parsed.city);
      if (parsed.state) parts.push(parsed.state);
      if (parsed.postalCode) parts.push(parsed.postalCode);
      if (parsed.country && parsed.country !== 'Nigeria') parts.push(parsed.country);
      return parts.length ? parts.join(', ') : 'Address not available';
    }

    if (typeof parsed === 'string' && parsed.trim()) return parsed;
    return 'Address not available';
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-muted text-muted-foreground border-transparent';
      case 'confirmed':
        return 'bg-secondary text-secondary-foreground border-transparent';
      case 'processing':
      case 'shipped':
        return 'bg-accent text-accent-foreground border-transparent';
      case 'delivered':
      case 'completed':
        return 'bg-primary text-primary-foreground border-transparent';
      case 'awaiting_buyer_confirmation':
        return 'bg-accent text-accent-foreground border-transparent';
      case 'cancelled':
      case 'failed':
      case 'refunded':
        return 'bg-destructive text-destructive-foreground border-transparent';
      default:
        return 'bg-muted text-muted-foreground border-transparent';
    }
  };

  const getPaymentBadgeClass = (status: string) => {
    switch (status) {
      case 'completed':
      case 'success':
        return 'bg-primary text-primary-foreground border-transparent';
      case 'failed':
        return 'bg-destructive text-destructive-foreground border-transparent';
      case 'pending':
      default:
        return 'bg-muted text-muted-foreground border-transparent';
    }
  };

  const getStatusSelectClass = (status: string) => {
    switch (status) {
      case 'delivered':
      case 'completed':
        return 'bg-primary text-primary-foreground';
      case 'cancelled':
        return 'bg-destructive text-destructive-foreground';
      case 'confirmed':
        return 'bg-secondary text-secondary-foreground';
      case 'shipped':
      case 'processing':
      case 'awaiting_buyer_confirmation':
        return 'bg-accent text-accent-foreground';
      case 'pending':
      default:
        return 'bg-muted';
    }
  };

  const fetchOrders = async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        // Fetch buyer profiles in bulk
        const buyerIds = Array.from(new Set(data.map((o: any) => o.buyer_id).filter(Boolean)));
        let buyersMap: Record<string, any> = {};
        
        if (buyerIds.length > 0) {
          const { data: buyers } = await supabase
            .from('profiles')
            .select('id, full_name, username, phone')
            .in('id', buyerIds);
          buyersMap = (buyers || []).reduce((acc: any, b: any) => {
            acc[b.id] = b;
            return acc;
          }, {});
        }

        // Collect all product IDs from orders to fetch their images
        const productIds = new Set<string>();
        data.forEach((order: any) => {
          const items = order.items || [];
          items.forEach((item: any) => {
            if (item.productId) productIds.add(item.productId);
          });
        });

        // Fetch product images in bulk
        let productsMap: Record<string, any> = {};
        if (productIds.size > 0) {
          const { data: products } = await supabase
            .from('products')
            .select('id, images, title')
            .in('id', Array.from(productIds));
          productsMap = (products || []).reduce((acc: any, p: any) => {
            acc[p.id] = p;
            return acc;
          }, {});
        }

        setOrders(
          data.map((order: any) => {
            const shippingAddress = safeJsonParse<any>(order.shipping_address) ?? order.shipping_address;
            const deliveryAddress = safeJsonParse<any>(order.delivery_address) ?? order.delivery_address;

            // Enrich items with product images if missing
            const enrichedItems = (order.items || []).map((item: any) => {
              const product = item.productId ? productsMap[item.productId] : null;
              return {
                ...item,
                // Add image from product if not present in item
                image: item.image || item.images?.[0] || (product?.images?.[0] || null),
                productName: item.productName || item.title || product?.title || 'Item',
              };
            });

            // Extract recipient info from multiple possible sources
            const extractedRecipientName = 
              order.recipient_name ||
              shippingAddress?.fullName ||
              shippingAddress?.name ||
              buyersMap[order.buyer_id]?.full_name ||
              buyersMap[order.buyer_id]?.username ||
              'Guest Customer';

            const extractedRecipientPhone = 
              order.recipient_phone ||
              shippingAddress?.phone ||
              buyersMap[order.buyer_id]?.phone ||
              null;

            return {
              id: order.id,
              buyerId: order.buyer_id,
              status: order.status || 'pending',
              paymentStatus: order.payment_status || 'pending',
              paymentMethod: order.payment_method || null,
              totalAmount: Number(order.total_amount),
              deliveryFee: Number(order.delivery_fee || 0),
              deliveryType: order.delivery_type || 'regular',
              paymentReference: order.payment_reference,
              createdAt: new Date(order.created_at),
              items: enrichedItems,
              shippingAddress,
              deliveryAddress: deliveryAddress || order.delivery_address,

              // Use extracted values for display
              customerName: extractedRecipientName,
              customerPhone: getFullPhone(extractedRecipientPhone),

              trackingNumber: order.tracking_number,
              estimatedDelivery: order.estimated_delivery,
              recipientName: extractedRecipientName,
              recipientPhone: getFullPhone(extractedRecipientPhone),
              deliveryAddressLine: order.delivery_address_line,
              deliveryCity: order.delivery_city,
              deliveryState: order.delivery_state,
              deliveryPhone: order.delivery_phone,
              deliveryId: order.delivery_id,
              deliveryStatus: order.delivery_status,
            };
          }),
        );
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast({
        title: "Error",
        description: "Failed to fetch orders",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId: string, newStatus: string, isSelfDelivery: boolean = false) => {
    try {
      const order = orders.find(o => o.id === orderId);
      
      // Auto-confirm if shipping a pending order
      if ((newStatus === 'shipped') && order?.status === 'pending') {
        await supabase
          .from('orders')
          .update({ 
            status: 'confirmed',
            updated_at: new Date().toISOString()
          })
          .eq('id', orderId);
      }

      // Self-delivery "Mark Delivered" → trigger buyer confirmation flow
      if (newStatus === 'delivered' && isSelfDelivery) {
        await handleSelfDeliveryMarkDelivered(orderId, order);
        return;
      }

      // Self-delivery shipped/processing → use server-validated RPC
      if (isSelfDelivery && (newStatus === 'shipped' || newStatus === 'processing')) {
        const { data: result, error: rpcError } = await supabase
          .rpc('update_self_delivery_status', {
            p_order_id: orderId,
            p_new_status: newStatus,
          });

        if (rpcError) throw rpcError;

        const rpcResult = result as any;
        if (!rpcResult?.success) {
          toast({
            title: "Action Blocked",
            description: rpcResult?.message || "Self-delivery is not enabled for this seller",
            variant: "destructive",
          });
          return;
        }

        // Update local state
        setOrders(orders.map(o => 
          o.id === orderId ? { ...o, status: newStatus } : o
        ));
      } else {
        // Non-self-delivery flow (unchanged)
        const updateData: Record<string, any> = { 
          status: newStatus,
          updated_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('orders')
          .update(updateData)
          .eq('id', orderId);

        if (error) throw error;

        // Update local state
        setOrders(orders.map(o => 
          o.id === orderId ? { ...o, status: newStatus } : o
        ));

        // Create tracking entry for non-self shipments
        if (newStatus === 'shipped') {
          const trackingNumber = `TRK${orderId.slice(0, 8).toUpperCase()}`;
          
          try {
            const { data: existingTracking } = await supabase
              .from('tracking')
              .select('id')
              .eq('order_id', orderId)
              .maybeSingle();

            if (existingTracking) {
              await supabase
                .from('tracking')
                .update({
                  tracking_number: trackingNumber,
                  status: 'in_transit',
                  description: 'Order has been shipped',
                  location: 'Lagos, Nigeria',
                  updated_at: new Date().toISOString()
                })
                .eq('order_id', orderId);
            } else {
              await supabase
                .from('tracking')
                .insert({
                  order_id: orderId,
                  tracking_number: trackingNumber,
                  status: 'in_transit',
                  description: 'Order has been shipped',
                  location: 'Lagos, Nigeria'
                });
            }
          } catch (trackingError) {
            console.error('Non-blocking tracking update failed:', trackingError);
          }

          try {
            await supabase
              .from('orders')
              .update({ tracking_number: trackingNumber })
              .eq('id', orderId);
          } catch (e) {
            console.error('Failed to update tracking number on order:', e);
          }
        }
      }

      // Send WhatsApp notification to customer
      if (order && order.customerPhone && order.customerPhone !== 'N/A') {
        const statusLabel = newStatus === 'shipped' ? (isSelfDelivery ? 'Out for Delivery (Self Delivery)' : 'Shipped') 
                          : newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
        
        try {
          await supabase.functions.invoke('send-whatsapp-notification', {
            body: {
              type: 'delivery-status-update',
              to: order.customerPhone,
              data: {
                orderNumber: orderId.slice(0, 8).toUpperCase(),
                status: statusLabel,
                statusMessage: isSelfDelivery && newStatus === 'shipped' 
                  ? 'The seller is personally delivering your order' 
                  : undefined,
                customerName: order.customerName,
                trackingUrl: `https://sabitradeng.com/track-order/${orderId}`
              }
            }
          });
        } catch (notifError) {
          console.error('Failed to send WhatsApp notification:', notifError);
        }
      }

      toast({
        title: "Status Updated",
        description: `Order status updated to ${newStatus}${isSelfDelivery ? ' (Self Delivery)' : ''}`,
      });
    } catch (error) {
      console.error('Error updating order status:', error);
      toast({
        title: "Error",
        description: "Failed to update order status",
        variant: "destructive",
      });
    }
  };

  const handleSelfDeliveryMarkDelivered = async (orderId: string, order: any) => {
    try {
      // Use server-side validated RPC for self-delivery
      const { data: result, error: rpcError } = await supabase
        .rpc('update_self_delivery_status', {
          p_order_id: orderId,
          p_new_status: 'delivered',
        });

      if (rpcError) throw rpcError;

      const rpcResult = result as any;
      if (!rpcResult?.success) {
        toast({
          title: "Action Blocked",
          description: rpcResult?.message || "Self-delivery is not enabled for this seller",
          variant: "destructive",
        });
        return;
      }

      const token = rpcResult.confirmation_token;

      // Update local state
      setOrders(orders.map(o => 
        o.id === orderId ? { ...o, status: 'awaiting_buyer_confirmation' } : o
      ));

      // Send buyer confirmation notifications (same flow as Fez)
      const confirmationUrl = `https://sabitradeng.com/confirm-delivery?orderId=${orderId}&token=${token}`;
      
      // Send WhatsApp notification
      if (order?.customerPhone && order.customerPhone !== 'N/A') {
        try {
          await supabase.functions.invoke('send-whatsapp-notification', {
            body: {
              type: 'delivery-confirmation-request',
              to: order.customerPhone,
              data: {
                orderNumber: orderId.slice(0, 8).toUpperCase(),
                customerName: order.customerName,
                confirmationUrl,
              }
            }
          });
        } catch (notifError) {
          console.error('Failed to send WhatsApp confirmation:', notifError);
        }
      }

      // Send email notification
      if (order?.buyerId) {
        try {
          await supabase.functions.invoke('send-transactional-email', {
            body: {
              type: 'delivery-confirmation',
              userId: order.buyerId,
              data: {
                orderNumber: orderId.slice(0, 8).toUpperCase(),
                customerName: order.customerName,
                confirmationUrl,
              }
            }
          });
        } catch (emailError) {
          console.error('Failed to send email confirmation:', emailError);
        }
      }

      // Send SMS notification
      if (order?.customerPhone && order.customerPhone !== 'N/A') {
        try {
          await supabase.functions.invoke('send-sms-notification', {
            body: {
              phone: order.customerPhone,
              message: `SabiTradeNG: Your order #${orderId.slice(0, 8).toUpperCase()} has been delivered! Please confirm delivery here: ${confirmationUrl}`
            }
          });
        } catch (smsError) {
          console.error('Failed to send SMS confirmation:', smsError);
        }
      }

      // Create in-app notification for buyer
      if (order?.buyerId) {
        try {
          await supabase.rpc('create_notification', {
            p_user_id: order.buyerId,
            p_title: 'Order Delivered!',
            p_message: `Your order #${orderId.slice(0, 8).toUpperCase()} has been delivered. Please confirm receipt.`,
            p_type: 'order',
            p_severity: 'info',
            p_metadata: { order_id: orderId },
            p_link: confirmationUrl,
          });
        } catch (notifError) {
          console.error('Failed to create in-app notification:', notifError);
        }
      }

      toast({
        title: "Marked as Delivered",
        description: "Buyer has been notified to confirm delivery. Funds will auto-release after 24 hours if not confirmed.",
      });

      await fetchOrders();
    } catch (error) {
      console.error('Error marking self-delivery as delivered:', error);
      toast({
        title: "Error",
        description: "Failed to mark order as delivered",
        variant: "destructive",
      });
    }
  };

  const handleCreateDelivery = async (orderId: string) => {
    setFulfillmentLoading(orderId);
    try {
      const order = orders.find(o => o.id === orderId);
      if (!order) throw new Error('Order not found');

      // Prevent duplicate delivery creation
      if (order.deliveryId) {
        toast({
          title: "Delivery already created",
          description: `This order already has a delivery (${order.deliveryId}). Tracking: ${order.trackingNumber || 'Pending'}`,
          variant: "destructive",
        });
        return;
      }

      // Validate required delivery address fields
      const missingFields = [];
      if (!order.deliveryAddressLine) missingFields.push('delivery address');
      if (!order.deliveryCity) missingFields.push('city');
      if (!order.deliveryState) missingFields.push('state');
      if (!order.deliveryPhone) missingFields.push('phone');

      if (missingFields.length > 0) {
        toast({
          title: "Customer delivery information incomplete",
          description: `Missing: ${missingFields.join(', ')}. The customer needs to provide complete delivery details.`,
          variant: "destructive",
        });
        return;
      }

      // Get seller pickup info from profile
      const { data: sellerProfile } = await supabase
        .from('profiles')
        .select('pickup_address_name, pickup_address_phone, pickup_address_email, pickup_address_street, pickup_address_city, pickup_address_state, business_name, phone')
        .eq('id', user?.id)
        .single();

      if (!sellerProfile?.pickup_address_state || !sellerProfile?.pickup_address_city) {
        throw new Error('Please set up your pickup location in Settings before creating a delivery.');
      }

      const response = await supabase.functions.invoke('fez-create-delivery', {
        body: {
          orderId: orderId,
          sellerPickup: {
            name: sellerProfile.pickup_address_name || sellerProfile.business_name || 'Seller',
            phone: sellerProfile.pickup_address_phone || sellerProfile.phone || '',
            email: sellerProfile.pickup_address_email || '',
            address: sellerProfile.pickup_address_street || '',
            city: sellerProfile.pickup_address_city,
            state: sellerProfile.pickup_address_state,
          },
          orderValue: order.totalAmount,
          items: (order.items || []).map((item: any) => ({
            name: item.productName || item.title || 'Item',
            quantity: item.quantity || 1,
            price: item.price || 0,
          })),
        }
      });

      if (response.error) throw response.error;

      const result = response.data;
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to create delivery');
      }

      toast({
        title: "Delivery Created",
        description: `Delivery booked with Fez. Tracking: ${result.tracking_number || 'Pending'}`,
      });

      await fetchOrders();
    } catch (error) {
      console.error('Error creating delivery:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create delivery",
        variant: "destructive",
      });
    } finally {
      setFulfillmentLoading(null);
    }
  };

  const formatLabel = (value: string) => value.replace(/_/g, ' ').toLowerCase();

  // Show paid orders and pay-on-delivery orders (not abandoned/pending online payment)
  const paidOrders = orders.filter(order =>
    order.paymentStatus === 'completed' || order.paymentStatus === 'success' ||
    order.paymentMethod === 'pay_on_delivery'
  );

  const filteredOrders = paidOrders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          order.paymentReference?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          order.customerName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          <p>Loading orders...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Order Management</CardTitle>
            <CardDescription>Manage and fulfill customer orders</CardDescription>
          </div>
          <div className="flex gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="awaiting_buyer_confirmation">Awaiting Confirmation</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search orders..."
                className="pl-10 w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredOrders.length === 0 ? (
          <div className="text-center py-10">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-muted-foreground">No orders found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order) => {
                const firstItem = order.items[0];
                const itemImage = firstItem ? getItemImage(firstItem) : null;
                
                return (
                <TableRow key={order.id}>
                  <TableCell className="font-mono text-sm">
                    {order.id.slice(0, 8).toUpperCase()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {itemImage ? (
                        <img 
                          src={itemImage} 
                          alt="Product" 
                          className="w-12 h-12 object-cover rounded-md border"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center">
                          <Package className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="text-sm">
                        {order.items.map((item: any, idx: number) => (
                          <div key={idx} className="truncate max-w-[150px]">
                            {item.quantity}x {item.productName || item.product?.title || item.title || 'Item'}
                          </div>
                        ))}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{order.customerName}</div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {order.customerPhone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm max-w-[180px] truncate" title={formatOrderDeliveryAddress(order)}>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{formatOrderDeliveryAddress(order)}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-semibold">
                    ₦{order.totalAmount.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={cn("capitalize", getPaymentBadgeClass(order.paymentStatus))}
                    >
                      {order.paymentMethod === 'pay_on_delivery' ? 'Pay on Delivery' : formatLabel(order.paymentStatus)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={order.status}
                      onValueChange={(value) => handleUpdateStatus(order.id, value)}
                    >
                      <SelectTrigger className={cn("w-32", getStatusSelectClass(order.status))}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="awaiting_buyer_confirmation">Awaiting Confirmation</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                    </TableCell>
                    <TableCell>
                      <OrderCountdownTimer 
                        orderCreatedAt={order.createdAt} 
                        orderStatus={order.status}
                        deadlineDays={2}
                      />
                    </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedOrder(order);
                          setIsOrderModalOpen(true);
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {(order.status === 'pending' || order.status === 'confirmed') && (order.paymentStatus === 'completed' || order.paymentMethod === 'pay_on_delivery') && !order.deliveryId && (
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleCreateDelivery(order.id)}
                            disabled={fulfillmentLoading === order.id}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                          >
                            <Truck className="h-4 w-4 mr-1" />
                            {fulfillmentLoading === order.id ? 'Creating...' : 'Ship with Fez'}
                          </Button>
                          {sellerDeliveryPreference === 'self' && (
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => handleUpdateStatus(order.id, 'shipped', true)}
                              disabled={fulfillmentLoading === order.id}
                              className="bg-secondary text-secondary-foreground border border-primary hover:bg-secondary/80"
                            >
                              <Truck className="h-4 w-4 mr-1" />
                              Self-Deliver
                            </Button>
                          )}
                        </div>
                      )}
                      {order.status === 'shipped' && order.paymentStatus === 'completed' && order.trackingNumber?.startsWith('SELF-') && sellerDeliveryPreference === 'self' && (
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={() => handleUpdateStatus(order.id, 'delivered', true)}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Mark Delivered
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Order Details Modal */}
      <Dialog open={isOrderModalOpen} onOpenChange={setIsOrderModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Order Details - {selectedOrder?.id.slice(0, 8).toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{selectedOrder.customerName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <p className="font-medium">{selectedOrder.customerPhone}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Details */}
              {(selectedOrder.shippingAddress || selectedOrder.recipientName || selectedOrder.deliveryAddress) && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg">Delivery Details</h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Recipient Name</p>
                      <p className="font-medium">
                        {selectedOrder.shippingAddress?.fullName || selectedOrder.recipientName || selectedOrder.customerName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        <p className="font-medium">
                          {selectedOrder.shippingAddress?.phone || selectedOrder.recipientPhone || selectedOrder.customerPhone}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground">Delivery Address</p>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 mt-1" />
                      <p className="font-medium">{formatOrderDeliveryAddress(selectedOrder)}</p>
                    </div>
                  </div>

                  {selectedOrder.shippingAddress?.deliveryInstructions && (
                    <div>
                      <p className="text-sm text-muted-foreground">Delivery Instructions</p>
                      <p className="text-sm">{selectedOrder.shippingAddress.deliveryInstructions}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Order Items */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Order Items</h3>
                <div className="space-y-4">
                  {selectedOrder.items.map((item: any, idx: number) => {
                    const itemImage = getItemImage(item);
                    return (
                    <div key={idx} className="flex items-center gap-4 p-4 border rounded-lg">
                      {itemImage ? (
                        <img
                          src={itemImage}
                          alt={item.productName || item.product?.title || 'Product'}
                          className="w-16 h-16 object-cover rounded-md border"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
                          <Package className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-grow">
                        <h4 className="font-medium">{item.productName || item.product?.title || item.title || 'Item'}</h4>
                        <p className="text-sm text-muted-foreground">Quantity: {item.quantity}</p>
                        <p className="text-sm text-muted-foreground">Price: ₦{item.price?.toLocaleString()}</p>
                        {item.size && <p className="text-sm text-muted-foreground">Size: {item.size}</p>}
                        {item.variation && <p className="text-sm text-muted-foreground">Variation: {item.variation}</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">₦{((item.price || 0) * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  )})}
                </div>
              </div>

              {/* Order Summary */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Order Summary</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Order Status</p>
                    <Badge
                      variant="outline"
                      className={cn("capitalize", getStatusBadgeClass(selectedOrder.status))}
                    >
                      {formatLabel(selectedOrder.status)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Status</p>
                    <Badge
                      variant="outline"
                      className={cn("capitalize", getPaymentBadgeClass(selectedOrder.paymentStatus))}
                    >
                      {selectedOrder.paymentMethod === 'pay_on_delivery' ? 'Pay on Delivery' : formatLabel(selectedOrder.paymentStatus)}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Amount</p>
                    <p className="font-semibold text-lg">₦{selectedOrder.totalAmount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Order Date</p>
                    <p className="font-medium">{selectedOrder.createdAt.toLocaleDateString()}</p>
                  </div>
                </div>
                
                {selectedOrder.paymentReference && (
                  <div>
                    <p className="text-sm text-muted-foreground">Payment Reference</p>
                    <p className="font-mono text-xs">{selectedOrder.paymentReference}</p>
                  </div>
                )}
                
                {selectedOrder.trackingNumber && (
                  <div>
                    <p className="text-sm text-muted-foreground">Tracking Number</p>
                    <p className="font-mono text-xs">{selectedOrder.trackingNumber}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}